import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Handshake, Users, Euro, BarChart3, FileText,
  Loader2, AlertTriangle, TrendingUp, Calendar,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TYPE_LABELS = { apporteur: "Apporteur d'affaires", revendeur: 'Revendeur', prescripteur: 'Prescripteur' };
const STATUT_COLORS = {
  gagne: 'bg-emerald-100 text-emerald-700',
  actif: 'bg-green-100 text-green-700',
  qualifie: 'bg-blue-100 text-blue-700',
  prix_valide: 'bg-purple-100 text-purple-700',
  nouveau: 'bg-gray-100 text-gray-700',
  essai: 'bg-amber-100 text-amber-700',
  en_cours: 'bg-cyan-100 text-cyan-700',
  perdu: 'bg-red-100 text-red-700',
};

export default function PartenairePublicPage() {
  const { accessToken } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/crm-vente-logiciel/partenaire-public/${accessToken}`);
        if (!res.ok) throw new Error('Lien invalide');
        setData(await res.json());
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [accessToken]);

  if (loading) return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-emerald-50 flex items-center justify-center">
      <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-red-50 flex items-center justify-center">
      <Card className="max-w-md w-full">
        <CardContent className="p-8 text-center">
          <AlertTriangle className="w-12 h-12 mx-auto text-red-400 mb-4" />
          <h2 className="text-xl font-bold mb-2">Lien invalide</h2>
          <p className="text-muted-foreground">Ce lien de partenaire n'existe pas ou a ete desactive.</p>
        </CardContent>
      </Card>
    </div>
  );

  const { partenaire, stats, prospects, rapports, historique_commissions } = data;

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-emerald-50" data-testid="partenaire-public-page">
      <div className="max-w-5xl mx-auto px-4 py-8 space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <div className="w-10 h-10 rounded-full bg-emerald-600 flex items-center justify-center">
                <Handshake className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold" data-testid="partenaire-public-nom">{partenaire.nom}</h1>
                {partenaire.entreprise && <p className="text-sm text-muted-foreground">{partenaire.entreprise}</p>}
              </div>
            </div>
            <Badge className="bg-emerald-100 text-emerald-700 mt-1">{TYPE_LABELS[partenaire.type_partenaire] || partenaire.type_partenaire}</Badge>
          </div>
          <div className="text-right">
            <p className="text-xs text-muted-foreground">Commission</p>
            <p className="text-3xl font-bold text-emerald-700" data-testid="partenaire-public-pct">{partenaire.commission_pct}%</p>
            <p className="text-xs text-muted-foreground">sur {partenaire.duree_commission_mois} mois</p>
          </div>
        </div>

        {/* Stats cards */}
        <div className="grid grid-cols-3 gap-4">
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="p-4 flex items-center gap-3">
              <Users className="w-6 h-6 text-blue-600" />
              <div>
                <p className="text-2xl font-bold text-blue-700" data-testid="stat-prospects">{stats.nb_prospects}</p>
                <p className="text-xs text-blue-600">Prospects apportes</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-emerald-50 border-emerald-200">
            <CardContent className="p-4 flex items-center gap-3">
              <Euro className="w-6 h-6 text-emerald-600" />
              <div>
                <p className="text-2xl font-bold text-emerald-700" data-testid="stat-commission">{stats.commission_mensuelle?.toFixed(2)} EUR</p>
                <p className="text-xs text-emerald-600">Commission mensuelle</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-amber-50 border-amber-200">
            <CardContent className="p-4 flex items-center gap-3">
              <FileText className="w-6 h-6 text-amber-600" />
              <div>
                <p className="text-2xl font-bold text-amber-700" data-testid="stat-rapports">{rapports?.length || 0}</p>
                <p className="text-xs text-amber-600">Rapports recus</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Prospects */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2"><TrendingUp className="w-4 h-4 text-emerald-600" />Mes prospects</CardTitle>
          </CardHeader>
          <CardContent>
            {prospects.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">Aucun prospect pour le moment</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="prospects-table">
                  <thead>
                    <tr className="border-b text-left text-muted-foreground text-xs">
                      <th className="pb-2 pr-4">Nom / Entreprise</th>
                      <th className="pb-2 pr-4">Date</th>
                      <th className="pb-2 pr-4">Statut</th>
                      <th className="pb-2 pr-4 text-right">Abonnement</th>
                      <th className="pb-2 text-right">Ma commission</th>
                    </tr>
                  </thead>
                  <tbody>
                    {prospects.map((p, i) => (
                      <tr key={i} className="border-b last:border-0 hover:bg-gray-50">
                        <td className="py-2.5 pr-4 font-medium">{p.nom}</td>
                        <td className="py-2.5 pr-4 text-muted-foreground">{p.date || '—'}</td>
                        <td className="py-2.5 pr-4">
                          <Badge className={`text-xs ${STATUT_COLORS[p.statut] || 'bg-gray-100 text-gray-700'}`}>{p.statut}</Badge>
                        </td>
                        <td className="py-2.5 pr-4 text-right">{p.prix_mensuel ? `${p.prix_mensuel} EUR/mois` : '—'}</td>
                        <td className="py-2.5 text-right font-semibold text-emerald-700">{p.commission ? `${p.commission} EUR` : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Historique commissions */}
        {historique_commissions?.length > 0 && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2"><BarChart3 className="w-4 h-4 text-emerald-600" />Historique des commissions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2" data-testid="historique-commissions">
                {historique_commissions.map((h, i) => (
                  <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors">
                    <div className="flex items-center gap-3">
                      <Calendar className="w-4 h-4 text-muted-foreground" />
                      <span className="font-medium">{h.mois}</span>
                      <span className="text-sm text-muted-foreground">{h.nb_prospects} prospect(s)</span>
                    </div>
                    <span className="font-bold text-emerald-700">{h.commission?.toFixed(2)} EUR</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Rapports post-RDV */}
        {rapports?.length > 0 && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2"><FileText className="w-4 h-4 text-emerald-600" />Rapports de rendez-vous</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2" data-testid="rapports-list">
                {rapports.map((r, i) => {
                  const scoreColor = r.score_chaleur >= 7 ? 'text-emerald-700 bg-emerald-100' : r.score_chaleur >= 4 ? 'text-amber-700 bg-amber-100' : 'text-red-700 bg-red-100';
                  return (
                    <div key={i} className="border rounded-lg p-3 hover:shadow-sm transition-shadow">
                      <div className="flex items-center justify-between mb-1">
                        <p className="font-medium text-sm">{r.prospect_nom}{r.prospect_entreprise ? ` — ${r.prospect_entreprise}` : ''}</p>
                        <span className="text-xs text-muted-foreground">{r.created_at?.slice(0, 10)}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={`text-xs ${scoreColor}`}>Score: {r.score_chaleur}/10</Badge>
                        <Badge variant="outline" className="text-xs">{r.statut_pipeline}</Badge>
                        {r.modules_suggeres?.slice(0, 3).map(m => (
                          <Badge key={m} variant="outline" className="text-xs">{m}</Badge>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Footer */}
        <div className="text-center text-xs text-muted-foreground pt-4 pb-8">
          Bureau Agricole — Espace partenaire
        </div>
      </div>
    </div>
  );
}
